import React, { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, CircularProgress,
  List, ListItem, ListItemText, Divider, Table, TableBody,
  TableRow, TableCell, Chip, Grid, Accordion, AccordionSummary,
  AccordionDetails, Checkbox, Collapse, ToggleButton, ToggleButtonGroup,
  Autocomplete, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import { searchRecordsOfService } from '../services/api';
import YearRangeSlider from './YearRangeSlider';

const currentYear = new Date().getFullYear();

// Map of ROSTypeID to readable names
const SERVICE_TYPE_NAMES = {
  1: 'Acting Ministries',
  3: 'Conferences, Delegations and Visits',
  4: 'Electorate',
  5: 'Ministerial Appointments',
  6: 'Parliamentary Appointments',
  7: 'Parliamentary Party Positions',
  8: 'Parliamentary Positions',
  9: 'Parliamentary Service',
  10: 'Party Positions',
  11: 'Parties Represented',
  12: 'State/Territory Government Service',
  13: 'Decorations',
  14: 'Honours',
  15: 'Local Government Service',
  16: 'Military Service',
  17: 'Miscellaneous Information',
  18: 'Occupations',
  19: 'Other Positions',
  20: 'Publications',
  21: 'Qualifications',
  22: 'Shadow Ministry Appointments',
  999: 'Committee Service Records'
};

export default function RecordsOfServiceSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchMode, setSearchMode] = useState(searchParams.get('mode') || 'simple');
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedDetails, setExpandedDetails] = useState({});
  const [activeFilters, setActiveFilters] = useState({});
  const [computedFacets, setComputedFacets] = useState({});

  // Advanced form state
  const [form, setForm] = useState({
    keywords: '',
    name: '',
    recordType: ''
  });
  const [dateYearRange, setDateYearRange] = useState([1850, currentYear]);

  // Available options for autocomplete
  const [availableOptions, setAvailableOptions] = useState({
    names: [],
    recordTypes: Object.entries(SERVICE_TYPE_NAMES).map(([id, name]) => ({ id, name }))
  });

  // Fetch autocomplete options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const data = await searchRecordsOfService('*', 1, 100);
        if (data.hits) {
          const names = [...new Set(data.hits.map(h => h.DisplayName).filter(Boolean))].sort();
          setAvailableOptions(prev => ({ ...prev, names }));
        }
      } catch (error) {
        console.error('Failed to fetch options:', error);
      }
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery && urlQuery !== '*') {
      handleSearch(urlQuery);
    } else {
      handleSearch('*');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const computeFacetsFromResults = (hits) => {
    const facets = {
      record_types: {}
    };

    hits.forEach(hit => {
      if (hit.ROSTypeID) {
        const typeName = SERVICE_TYPE_NAMES[hit.ROSTypeID] || `Type ${hit.ROSTypeID}`;
        facets.record_types[typeName] = (facets.record_types[typeName] || 0) + 1;
      }
    });

    const result = {};
    Object.entries(facets).forEach(([name, counts]) => {
      result[name] = Object.entries(counts)
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count);
    });
    return result;
  };

  const handleChange = (field) => (event) => {
    setForm({ ...form, [field]: event.target.value });
  };

  const handleAutocompleteChange = (field) => (event, newValue) => {
    setForm({ ...form, [field]: newValue || '' });
  };

  const buildAdvancedQuery = () => {
    const parts = [];

    if (form.keywords) parts.push(`(${form.keywords})`);
    if (form.name) parts.push(`DisplayName:"${form.name}"`);
    if (form.recordType) {
      // Find the ID for this record type name
      const entry = Object.entries(SERVICE_TYPE_NAMES).find(([, name]) => name === form.recordType);
      if (entry) {
        parts.push(`ROSTypeID:${entry[0]}`);
      }
    }

    if (dateYearRange[0] > 1850 || dateYearRange[1] < currentYear) {
      parts.push(`DateStart1:[${dateYearRange[0]}-01-01 TO ${dateYearRange[1]}-12-31]`);
    }

    return parts.length > 0 ? parts.join(' AND ') : '*';
  };

  const buildQueryWithFilters = (baseQuery, filters) => {
    const filterParts = [];
    Object.entries(filters).forEach(([field, values]) => {
      if (values.length > 0 && field === 'record_types') {
        // Convert type names back to IDs
        const typeIds = values.map(v => {
          const entry = Object.entries(SERVICE_TYPE_NAMES).find(([, name]) => name === v);
          return entry ? entry[0] : null;
        }).filter(Boolean);
        if (typeIds.length > 0) {
          const valueParts = typeIds.map(id => `ROSTypeID:${id}`).join(' OR ');
          filterParts.push(`(${valueParts})`);
        }
      }
    });

    if (filterParts.length === 0) return baseQuery;
    if (baseQuery === '*') return filterParts.join(' AND ');
    return `(${baseQuery}) AND ${filterParts.join(' AND ')}`;
  };

  const handleSearch = async (searchQuery = null, filters = activeFilters) => {
    let baseQuery;
    if (searchMode === 'simple') {
      baseQuery = searchQuery !== null ? searchQuery : (query || '*');
    } else {
      baseQuery = buildAdvancedQuery();
    }

    setLoading(true);
    try {
      const fullQuery = buildQueryWithFilters(baseQuery, filters);
      const data = await searchRecordsOfService(fullQuery, 1, 100);
      setResults(data);
      const facets = computeFacetsFromResults(data.hits);
      setComputedFacets(facets);
      setSearchParams({ q: baseQuery, mode: searchMode });
    } catch (error) {
      console.error('Search error:', error);
    }
    setLoading(false);
  };

  const handleReset = () => {
    setQuery('');
    setForm({
      keywords: '',
      name: '',
      recordType: ''
    });
    setDateYearRange([1850, currentYear]);
    setActiveFilters({});
    setResults(null);
    setSearchParams({});
  };

  const handleFacetClick = (field, value) => {
    const newFilters = { ...activeFilters };
    if (!newFilters[field]) newFilters[field] = [];
    const valueIndex = newFilters[field].indexOf(value);
    if (valueIndex > -1) {
      newFilters[field] = newFilters[field].filter(v => v !== value);
      if (newFilters[field].length === 0) delete newFilters[field];
    } else {
      newFilters[field].push(value);
    }
    setActiveFilters(newFilters);
    handleSearch(null, newFilters);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    handleSearch(null, {});
  };

  const toggleDetails = (id) => {
    setExpandedDetails(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === '1900-01-01') return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const MetadataRow = ({ label, value }) => {
    if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) return null;
    return (
      <TableRow>
        <TableCell sx={{ fontWeight: 500, color: 'text.secondary', width: '30%', py: 1 }}>{label}</TableCell>
        <TableCell sx={{ py: 1 }}>{Array.isArray(value) ? value.join(', ') : String(value)}</TableCell>
      </TableRow>
    );
  };

  const getRecordSummary = (hit) => {
    // Return the most relevant text field
    return hit.Text1 || hit.Value1 || hit.Value2 || '';
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSearchMode(newMode);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Records of Service</Typography>
        <ToggleButtonGroup
          value={searchMode}
          exclusive
          onChange={handleModeChange}
          size="small"
        >
          <ToggleButton value="simple">
            <SearchIcon sx={{ mr: 1 }} /> Simple
          </ToggleButton>
          <ToggleButton value="advanced">
            <TuneIcon sx={{ mr: 1 }} /> Advanced
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Simple Search */}
      <Collapse in={searchMode === 'simple'}>
        <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              placeholder="Search service records..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
            <Button
              variant="contained"
              onClick={() => handleSearch()}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ borderRadius: 3, px: 4 }}
            >
              Search
            </Button>
            <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 3 }}>
              Reset
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Advanced Search */}
      <Collapse in={searchMode === 'advanced'}>
        <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords"
                value={form.keywords}
                onChange={handleChange('keywords')}
                placeholder="Search across all fields"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.names}
                value={form.name}
                onChange={handleAutocompleteChange('name')}
                renderInput={(params) => <TextField {...params} label="Person Name" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Record Type</InputLabel>
                <Select value={form.recordType} onChange={handleChange('recordType')} label="Record Type">
                  <MenuItem value="">Any</MenuItem>
                  {Object.entries(SERVICE_TYPE_NAMES).map(([id, name]) => (
                    <MenuItem key={id} value={name}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <YearRangeSlider
                label="Date Range"
                value={dateYearRange}
                onChange={setDateYearRange}
                min={1850}
                max={currentYear}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => handleSearch()}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Search
                </Button>
                <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 2 }}>
                  Reset
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  Query: <code>{buildAdvancedQuery() || '(empty)'}</code>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {results && (
        <Grid container spacing={3}>
          {Object.keys(computedFacets).length > 0 && (
            <Grid item xs={12} md={3}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3, position: 'sticky', top: 16 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Filter Results
                  </Typography>
                  {Object.keys(activeFilters).length > 0 && (
                    <Button size="small" onClick={clearAllFilters}>Clear All</Button>
                  )}
                </Box>

                {Object.entries(computedFacets).map(([facetName, facetValues]) => (
                  facetValues.length > 0 && (
                    <Accordion key={facetName} defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">
                          Record Types
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <List dense sx={{ py: 0, maxHeight: 300, overflow: 'auto' }}>
                          {facetValues.map((facet) => (
                            <ListItem key={facet.value} disablePadding>
                              <Checkbox
                                size="small"
                                checked={activeFilters[facetName]?.includes(facet.value) || false}
                                onChange={() => handleFacetClick(facetName, facet.value)}
                              />
                              <ListItemText
                                primary={`${facet.value} (${facet.count})`}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )
                ))}
              </Paper>
            </Grid>
          )}

          <Grid item xs={12} md={Object.keys(computedFacets).length > 0 ? 9 : 12}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {results.total} service records found
              </Typography>
              <List>
                {results.hits.map((hit, index) => (
                  <React.Fragment key={hit.id}>
                    {index > 0 && <Divider />}
                    <ListItem alignItems="flex-start" sx={{ flexDirection: 'column' }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography variant="resultTitle">
                              {hit.DisplayName}
                            </Typography>
                            <Chip
                              label={SERVICE_TYPE_NAMES[hit.ROSTypeID] || `Type ${hit.ROSTypeID}`}
                              size="small"
                              color="default"
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}>
                              {getRecordSummary(hit)}
                            </Typography>
                            {(formatDate(hit.DateStart1) || formatDate(hit.DateEnd1)) && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {formatDate(hit.DateStart1)} - {formatDate(hit.DateEnd1) || 'Present'}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <Button size="small" onClick={() => toggleDetails(hit.id)} sx={{ mt: 1 }}>
                        {expandedDetails[hit.id] ? 'Hide details' : 'Show all details'}
                      </Button>
                      <Collapse in={expandedDetails[hit.id]}>
                        <Box sx={{ mt: 2, width: '100%' }}>
                          <Table size="small">
                            <TableBody>
                              <TableRow>
                                <TableCell colSpan={2} sx={{ bgcolor: '#f5f5f5', fontWeight: 600 }}>
                                  Record Details
                                </TableCell>
                              </TableRow>
                              <MetadataRow label="Person" value={hit.DisplayName} />
                              <MetadataRow label="PHID" value={hit.PHID} />
                              <MetadataRow label="Record Type" value={SERVICE_TYPE_NAMES[hit.ROSTypeID] || `Type ${hit.ROSTypeID}`} />
                              <MetadataRow label="Order" value={hit.OBY} />
                              <TableRow>
                                <TableCell colSpan={2} sx={{ bgcolor: '#f5f5f5', fontWeight: 600 }}>
                                  Values
                                </TableCell>
                              </TableRow>
                              <MetadataRow label="Value 1" value={hit.Value1} />
                              <MetadataRow label="Value 2" value={hit.Value2} />
                              <MetadataRow label="Value 3" value={hit.Value3} />
                              <MetadataRow label="Abbreviation" value={hit.ValueAbbrev1 !== 'default' ? hit.ValueAbbrev1 : null} />
                              <TableRow>
                                <TableCell colSpan={2} sx={{ bgcolor: '#f5f5f5', fontWeight: 600 }}>
                                  Text Details
                                </TableCell>
                              </TableRow>
                              <MetadataRow label="Text 1" value={hit.Text1} />
                              <MetadataRow label="Text 2" value={hit.Text2} />
                              <MetadataRow label="Text 3" value={hit.Text3} />
                              <TableRow>
                                <TableCell colSpan={2} sx={{ bgcolor: '#f5f5f5', fontWeight: 600 }}>
                                  Dates
                                </TableCell>
                              </TableRow>
                              <MetadataRow label="Start Date 1" value={formatDate(hit.DateStart1)} />
                              <MetadataRow label="End Date 1" value={formatDate(hit.DateEnd1)} />
                              <MetadataRow label="Start Date 2" value={formatDate(hit.DateStart2)} />
                              <MetadataRow label="End Date 2" value={formatDate(hit.DateEnd2)} />
                              {hit.MpOrSenator && <MetadataRow label="Chamber" value={hit.MpOrSenator} />}
                            </TableBody>
                          </Table>
                          <Box sx={{ mt: 2 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              component={RouterLink}
                              to={`/parliamentarians?q=PHID:"${hit.PHID}"`}
                            >
                              View Parliamentarian
                            </Button>
                          </Box>
                        </Box>
                      </Collapse>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
